import React, { ReactNode, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { getRemainingVisualViewportHeight } from '@src/packages/utils';

import getAvailablePaneHeight from './getAvailablePaneHeight';
import styles from './styles.module.less';

const LIST_VIEWPORT_AVAILABLE_VAR = '--list-viewport-available';

interface ListViewportLayoutProps {
  children: ReactNode;
  top?: ReactNode;
  footer?: ReactNode;
  className?: string;
  bodyClassName?: string;
  topClassName?: string;
  footerClassName?: string;
  viewportOffset?: number | string;
  minHeight?: number | string;
  /** Right padding for list area when a side panel (e.g. filters) is open. */
  sidePanelOpen?: boolean;
  scrollable?: boolean;
  fillViewport?: boolean;
  /** Keep the pane inside the nearest scroll parent instead of measuring to the viewport. */
  constrainToScrollParent?: boolean;
  /** Ref to the scrollable body for view-specific wheel handling. */
  bodyRef?: React.Ref<HTMLDivElement>;
  /** Let long-form content grow naturally instead of clipping it to the viewport. */
  fitContent?: boolean;
}

const toCssValue = (value: number | string) => {
  return typeof value === 'number' ? `${value}px` : value;
};

const ListViewportLayout: React.FC<ListViewportLayoutProps> = ({
  children,
  top,
  footer,
  className = '',
  bodyClassName = '',
  topClassName = '',
  footerClassName = '',
  viewportOffset = 180,
  minHeight = 320,
  sidePanelOpen = false,
  scrollable = true,
  fillViewport = false,
  constrainToScrollParent = true,
  fitContent = false,
  bodyRef,
}) => {
  const rootRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return undefined;
    }

    let frameId = 0;

    const syncAvailableHeight = () => {
      if (frameId) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const available = fillViewport
          ? getRemainingVisualViewportHeight(el.getBoundingClientRect().top, viewportHeight)
          : getAvailablePaneHeight(el, viewportHeight, constrainToScrollParent);
        const nextValue = `${available}px`;
        if (el.style.getPropertyValue(LIST_VIEWPORT_AVAILABLE_VAR) === nextValue) {
          return;
        }
        el.style.setProperty(LIST_VIEWPORT_AVAILABLE_VAR, nextValue);
      });
    };

    syncAvailableHeight();
    // Header/filter rows can settle after the first layout pass on a hard reload.
    const settleFrameId = fillViewport ? window.requestAnimationFrame(syncAvailableHeight) : 0;

    const viewport = window.visualViewport;
    const layoutObserver = fillViewport && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(syncAvailableHeight)
      : null;
    if (el.parentElement) layoutObserver?.observe(el.parentElement);
    viewport?.addEventListener('resize', syncAvailableHeight);
    viewport?.addEventListener('scroll', syncAvailableHeight);
    window.addEventListener('resize', syncAvailableHeight);
    window.addEventListener('orientationchange', syncAvailableHeight);

    return () => {
      window.cancelAnimationFrame(frameId);
      if (settleFrameId) window.cancelAnimationFrame(settleFrameId);
      layoutObserver?.disconnect();
      viewport?.removeEventListener('resize', syncAvailableHeight);
      viewport?.removeEventListener('scroll', syncAvailableHeight);
      window.removeEventListener('resize', syncAvailableHeight);
      window.removeEventListener('orientationchange', syncAvailableHeight);
      el.style.removeProperty(LIST_VIEWPORT_AVAILABLE_VAR);
    };
  }, [constrainToScrollParent, fillViewport]);

  return (
    <section
      ref={rootRef}
      className={classNames(
        styles.root,
        fitContent && styles.fitContent,
        sidePanelOpen && styles.rootSidePanelOpen,
        className,
      )}
      style={
        {
          '--list-viewport-offset': toCssValue(viewportOffset),
          '--list-viewport-min-height': toCssValue(minHeight),
        } as React.CSSProperties
      }
    >
      {top ? <div className={classNames(styles.top, topClassName)}>{top}</div> : <div />}
      <div ref={bodyRef} className={classNames(styles.body, bodyClassName, scrollable && styles.scrollable)}>
        {children}
      </div>
      {footer ? <div className={classNames(styles.footer, footerClassName)}>{footer}</div> : null}
    </section>
  );
};

export default React.memo(ListViewportLayout);
